import { useEffect, useRef } from 'react';
import { Flag } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { StepState, WorkflowPhase } from '@/lib/workflowStreamProtocol';

interface StepRailProps {
  steps: StepState[];
  activeStepId: string | null;
  workflowPhase?: WorkflowPhase;
  onSelectStep?: (stepId: string) => void;
}

const NODE_WIDTH = 56; // px — fixed width per step so the rail can scroll cleanly

export function StepRail({ steps, activeStepId, workflowPhase, onSelectStep }: StepRailProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll so the active step is centered in the rail viewport when it
  // changes (e.g., on Proceed advancing the active step).
  useEffect(() => {
    if (!activeStepId || !scrollerRef.current) return;
    const idx = steps.findIndex(s => s.id === activeStepId);
    if (idx < 0) return;
    const scroller = scrollerRef.current;
    const target = idx * NODE_WIDTH;
    const visible = scroller.clientWidth;
    // Center the node when possible
    scroller.scrollTo({ left: Math.max(0, target - visible / 2 + NODE_WIDTH / 2), behavior: 'smooth' });
  }, [activeStepId, steps]);

  if (steps.length === 0) {
    return (
      <div className="text-xs text-muted-foreground text-center py-3">
        Canvas populates as Cosmo proposes your workflow.
      </div>
    );
  }

  return (
    <div
      ref={scrollerRef}
      className="relative overflow-x-auto overflow-y-hidden py-3 -mx-2 px-2 scrollbar-thin"
      style={{ scrollbarWidth: 'thin' }}
    >
      {/* Connector line drawn ONCE behind all the nodes (including the finish marker), sized to the row. */}
      <div
        className="absolute top-1/2 h-px bg-border -translate-y-1/2 z-0"
        style={{
          left: NODE_WIDTH / 2,
          width: Math.max(0, steps.length * NODE_WIDTH),
        }}
        aria-hidden
      />
      <div
        className="relative z-10 flex items-center"
        style={{ width: (steps.length + 1) * NODE_WIDTH, minWidth: '100%' }}
      >
        {steps.map((step, idx) => {
          const isActive = step.id === activeStepId;
          const isDone = step.phase === 'done';
          const isProposed = step.phase === 'proposed';
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => onSelectStep?.(step.id)}
              className="flex flex-col items-center gap-1 group shrink-0"
              style={{ width: NODE_WIDTH }}
              aria-label={`Step ${idx + 1}: ${step.stepName}`}
            >
              <div
                className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono tabular-nums border bg-card transition-colors',
                  isDone && 'bg-success border-success text-success-foreground',
                  isActive && 'bg-primary border-primary text-primary-foreground ring-4 ring-primary/20',
                  isProposed && !isActive && 'border-dashed text-muted-foreground',
                )}
              >
                {isDone ? '✓' : idx + 1}
              </div>
              <span
                className={cn(
                  'text-[10px] truncate w-full text-center transition-opacity',
                  isActive ? 'text-primary opacity-100' : 'text-muted-foreground opacity-0 group-hover:opacity-100',
                )}
              >
                {step.stepName}
              </span>
            </button>
          );
        })}

        {/* Finish endpoint. Only turns success-green once the workflow phase
            is actually 'finished' (i.e. the user proceeded past the last step
            via the analysis card's Finish button). Until then it's a muted
            dashed node, matching the look of unstarted future steps. */}
        {(() => {
          const finished = workflowPhase === 'finished';
          return (
            <div
              role="img"
              className="flex flex-col items-center gap-1 shrink-0"
              style={{ width: NODE_WIDTH }}
              aria-label="Workflow finish"
            >
              <div
                className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center border bg-card transition-colors',
                  finished
                    ? 'bg-success border-success text-success-foreground'
                    : 'border-dashed text-muted-foreground',
                )}
              >
                <Flag className="h-3 w-3" />
              </div>
              <span
                className={cn(
                  'text-[10px] w-full text-center',
                  finished ? 'text-success-foreground/80' : 'text-muted-foreground',
                )}
              >
                {finished ? 'done' : 'finish'}
              </span>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
